import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, 
  Shield, 
  Trash2, 
  LogOut, 
  Loader2, 
  GraduationCap,
  Search,
  UserCog,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { authGetMe, authLogout, adminListUsers, adminDeleteUser, adminUpdateRole, adminValidateUser, adminInvalidateUser } from "../lib/api";
import { toast } from "sonner";

const ROLE_COLORS = {
  admin: "bg-red-100 text-red-700 border-red-200",
  teacher: "bg-blue-100 text-blue-700 border-blue-200",
  student: "bg-green-100 text-green-700 border-green-200",
  parent: "bg-purple-100 text-purple-700 border-purple-200",
};

const ROLE_LABELS = {
  admin: "Admin",
  teacher: "Enseignant",
  student: "Élève",
  parent: "Parent",
};

const VALIDATION_BADGE = {
  true: "bg-green-100 text-green-700 border-green-200",
  false: "bg-orange-100 text-orange-700 border-orange-200",
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const checkAuthAndLoadData = async () => {
    try {
      // Check if user is authenticated and is admin
      const { data: me } = await authGetMe();
      
      if (me.role !== "admin") {
        toast.error("Accès réservé aux administrateurs");
        navigate("/");
        return;
      }
      
      setCurrentUser(me);
      
      // Load users
      const { data: usersList } = await adminListUsers();
      setUsers(usersList);
    } catch (err) {
      console.error("Auth check failed:", err);
      toast.error("Veuillez vous connecter");
      navigate("/auth");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authLogout();
      localStorage.removeItem("navi_user_id");
      localStorage.removeItem("navi_user_email");
      localStorage.removeItem("navi_user_name");
      localStorage.removeItem("navi_user_role");
      toast.success("Déconnexion réussie");
      navigate("/auth");
    } catch (err) {
      console.error("Logout error:", err);
      navigate("/auth");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdating(userId);
    try {
      await adminUpdateRole(userId, newRole);
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success("Rôle mis à jour");
    } catch (err) {
      console.error("Role update error:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setUpdating(null);
    }
  };

  const handleValidationToggle = async (userId, currentlyValidated) => {
    setUpdating(userId);
    try {
      if (currentlyValidated) {
        await adminInvalidateUser(userId);
        setUsers(users.map(u => u.id === userId ? { ...u, is_validated: false } : u));
        toast.success("Compte suspendu");
      } else {
        await adminValidateUser(userId);
        setUsers(users.map(u => u.id === userId ? { ...u, is_validated: true } : u));
        toast.success("Compte validé");
      }
    } catch (err) {
      console.error("Validation error:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.user) return;
    
    try {
      await adminDeleteUser(deleteDialog.user.id);
      setUsers(users.filter(u => u.id !== deleteDialog.user.id));
      toast.success("Utilisateur supprimé");
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleteDialog({ open: false, user: null });
    }
  };

  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-800">Administration</h1>
                <p className="text-xs text-slate-500">L'École de Navi</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-800">{currentUser?.name}</p>
                <p className="text-xs text-slate-500">{currentUser?.email}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{users.length}</p>
                <p className="text-xs text-slate-500">Total utilisateurs</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {users.filter(u => u.role === "admin").length}
                </p>
                <p className="text-xs text-slate-500">Admins</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {users.filter(u => u.role === "teacher").length}
                </p>
                <p className="text-xs text-slate-500">Enseignants</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {users.filter(u => u.role === "student" || u.role === "parent").length}
                </p>
                <p className="text-xs text-slate-500">Élèves/Parents</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Search Header */}
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher un utilisateur..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="admin-search-input"
                />
              </div>
              <Badge variant="secondary" className="hidden sm:flex">
                {filteredUsers.length} résultat(s)
              </Badge>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-users-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Créé le
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full flex items-center justify-center text-white font-bold">
                          {user.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{user.name || "Sans nom"}</p>
                          {user.id === currentUser?.id && (
                            <span className="text-xs text-indigo-600">(Vous)</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-slate-600">{user.email || "-"}</p>
                    </td>
                    <td className="px-4 py-4">
                      {user.id === currentUser?.id ? (
                        <Badge className={ROLE_COLORS[user.role]}>
                          {ROLE_LABELS[user.role] || user.role}
                        </Badge>
                      ) : (
                        <Select
                          value={user.role}
                          onValueChange={(value) => handleRoleChange(user.id, value)}
                          disabled={updating === user.id}
                        >
                          <SelectTrigger className="w-32 h-8">
                            {updating === user.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <SelectValue />
                            )}
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="teacher">Enseignant</SelectItem>
                            <SelectItem value="student">Élève</SelectItem>
                            <SelectItem value="parent">Parent</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {user.role === "admin" ? (
                        <Badge className={VALIDATION_BADGE.true}>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      ) : user.is_validated ? (
                        <Badge className={VALIDATION_BADGE.true} data-testid={`status-validated-${user.id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Validé
                        </Badge>
                      ) : (
                        <Badge className={VALIDATION_BADGE.false} data-testid={`status-pending-${user.id}`}>
                          <AlertCircle className="w-3 h-3 mr-1" />
                          En attente
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-slate-500">
                        {user.created_at 
                          ? new Date(user.created_at).toLocaleDateString("fr-FR")
                          : "-"
                        }
                      </p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {user.id !== currentUser?.id && user.role !== "admin" && (
                          <Button
                            variant={user.is_validated ? "outline" : "default"}
                            size="sm"
                            onClick={() => handleValidationToggle(user.id, user.is_validated)}
                            disabled={updating === user.id}
                            className={user.is_validated 
                              ? "text-orange-600 border-orange-200 hover:bg-orange-50" 
                              : "bg-green-600 hover:bg-green-700 text-white"}
                            data-testid={`toggle-validate-${user.id}`}
                          >
                            {updating === user.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : user.is_validated ? (
                              "Suspendre"
                            ) : (
                              "Valider"
                            )}
                          </Button>
                        )}
                        {user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, user })}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      Aucun utilisateur trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de supprimer l'utilisateur{" "}
              <strong>{deleteDialog.user?.name || deleteDialog.user?.email}</strong>.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
